import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
} from 'react'

import { XStack, Input } from 'tamagui'

import { Button } from '../Button/Button'
import { Icon } from '../Icon'
import { useDynamicIsland } from '../DynamicIsland/useDynamicIsland'
import { SearchOverlay } from './SearchOverlay'

const SEARCH_ROW_ID = 'search-overlay'

const PLACEHOLDERS = [
  'Start typing, or just hit Enter',
  'Events nearby, new Guilds, your next talk',
  'React meetups, AI talks, your next event',
  'Type a vibe, find your people',
  'Search anything or create something',
  "There's always something happening",
  'Your next favourite event is one search away',
  'Find your people',
]

const randomPlaceholder = () =>
  PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)]

// Measure text width using an off-screen canvas with the input's actual font.
let _measureCanvas: HTMLCanvasElement | null = null
const measureTextWidth = (text: string, font: string): number => {
  if (typeof document === 'undefined') return 0
  if (!_measureCanvas) _measureCanvas = document.createElement('canvas')
  const ctx = _measureCanvas.getContext('2d')
  if (!ctx) return 0
  ctx.font = font
  return Math.ceil(ctx.measureText(text).width)
}

const SearchInput = ({
  inputRef,
  onValueChange,
  onEscape,
  setSearchTextRef,
  placeholder,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputRef: React.RefObject<any>
  onValueChange: (value: string) => void
  onEscape: () => void
  setSearchTextRef: React.MutableRefObject<((text: string) => void) | null>
  placeholder: string
}) => {
  const [value, setValue] = useState('')

  // Register a setter so the parent can programmatically update the input value
  useEffect(() => {
    setSearchTextRef.current = (text: string) => {
      setValue(text)
      onValueChange(text)
    }
    return () => {
      setSearchTextRef.current = null
    }
  }, [onValueChange, setSearchTextRef])
  const [inputMinWidth, setInputMinWidth] = useState<number | undefined>(
    undefined
  )
  const computedFontRef = useRef('')
  const naturalWidthRef = useRef(0)
  const placeholderWidthRef = useRef(0)

  // Capture the input's actual computed font, natural width, and placeholder width after mount
  useEffect(() => {
    requestAnimationFrame(() => {
      const el = inputRef.current as unknown as HTMLElement | null
      if (!el) return
      const cs = window.getComputedStyle(el)
      computedFontRef.current = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`
      naturalWidthRef.current = el.clientWidth
      // Measure placeholder so we can ensure it's fully visible
      const pw = measureTextWidth(placeholder, computedFontRef.current) + 12
      const maxWidth =
        typeof window !== 'undefined'
          ? Math.min(560, window.innerWidth - 148)
          : 400
      placeholderWidthRef.current = Math.min(pw, maxWidth)
      if (pw > naturalWidthRef.current) {
        setInputMinWidth(placeholderWidthRef.current)
      }
    })
  }, [inputRef])

  const handleChangeText = useCallback(
    (text: string) => {
      setValue(text)
      onValueChange(text)
    },
    [onValueChange]
  )

  // Measure typed text using the input's real font. Only expand beyond the
  // natural flex width — never contract below it. When empty, use placeholder width.
  useLayoutEffect(() => {
    if (!value || !computedFontRef.current) {
      // When empty, keep placeholder width so it doesn't get clipped
      if (placeholderWidthRef.current > naturalWidthRef.current) {
        setInputMinWidth(placeholderWidthRef.current)
      } else {
        setInputMinWidth(undefined)
      }
      return
    }
    const textWidth = measureTextWidth(value, computedFontRef.current)
    const needed = textWidth + 12 // cursor + breathing room
    if (needed > naturalWidthRef.current) {
      const maxWidth =
        typeof window !== 'undefined'
          ? Math.min(560, window.innerWidth - 148)
          : 400
      setInputMinWidth(Math.min(needed, maxWidth))
    } else {
      setInputMinWidth(undefined)
    }
  }, [value])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault()
          onEscape()
          break
        case 'ArrowDown':
        case 'ArrowUp':
        case 'Enter':
          // Let these propagate to the global listener in SearchOverlay
          // but prevent the input's default behavior (cursor movement, form submit)
          e.preventDefault()
          break
      }
    },
    [onEscape]
  )

  return (
    <XStack flex={1} alignItems='center' gap='$2' paddingHorizontal='$2'>
      <Icon name='prompt' color='$colorSubtle' size='$5' />
      <Input
        ref={inputRef}
        flex={1}
        value={value}
        onChangeText={handleChangeText}
        placeholder={placeholder}
        borderWidth={0}
        outlineWidth={0}
        borderRadius={0}
        backgroundColor='transparent'
        fontSize='$5'
        height='$8'
        padding='$0'
        focusStyle={{ borderWidth: 0, outlineWidth: 0 }}
        hoverStyle={{ borderWidth: 0, outlineWidth: 0 }}
        {...(inputMinWidth ? { minWidth: inputMinWidth } : {})}
        {...{ onKeyDown: handleKeyDown }}
      />
    </XStack>
  )
}

export const SearchProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const inputRef = useRef<any>(null)
  const setSearchTextRef = useRef<((text: string) => void) | null>(null)
  const { pushRows, popRows } = useDynamicIsland()

  // Stable refs for callbacks used inside pushed rows
  const onValueChangeRef = useRef(setInputValue)
  const closeSearchRef = useRef<() => void>(() => {})

  const populateSearch = useCallback((text: string) => {
    setInputValue(text)
    setSearchTextRef.current?.(text)
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [])

  const closeSearch = useCallback(() => {
    setIsOpen(false)
    setInputValue('')
    popRows(SEARCH_ROW_ID)
  }, [popRows])

  closeSearchRef.current = closeSearch

  const openSearch = useCallback(() => {
    setIsOpen(true)
    setInputValue('')
    pushRows(
      [
        [
          {
            id: 'search-input',
            component: (
              <SearchInput
                inputRef={inputRef}
                onValueChange={(v) => onValueChangeRef.current(v)}
                onEscape={() => closeSearchRef.current()}
                setSearchTextRef={setSearchTextRef}
                placeholder={randomPlaceholder()}
              />
            ),
            containerStyle: { flex: 1 },
          },
          {
            id: 'search-close',
            component: (
              <Button
                minHeight='$8'
                height='$8'
                width='$8'
                padding='$0'
                alignItems='center'
                justifyContent='center'
                secondary
                onPress={() => closeSearchRef.current()}
              >
                <Icon
                  name='add'
                  color='$color'
                  transform={[{ rotateZ: '45deg' }]}
                />
              </Button>
            ),
          },
        ],
      ],
      SEARCH_ROW_ID
    )
  }, [pushRows])

  // Auto-focus the input when search opens
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        inputRef.current?.focus()
      })
    }
  }, [isOpen])

  // Global keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        if (isOpen) {
          closeSearch()
        } else {
          openSearch()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, openSearch, closeSearch])

  // Listen for custom event from search icon in Dynamic Island
  useEffect(() => {
    const handler = () => openSearch()
    window.addEventListener('guild-open-search', handler)
    return () => window.removeEventListener('guild-open-search', handler)
  }, [openSearch])

  return (
    <>
      {children}
      {isOpen && (
        <SearchOverlay
          inputValue={inputValue}
          onClose={closeSearch}
          onPopulateSearch={populateSearch}
        />
      )}
    </>
  )
}
